import { useEffect, useMemo, useState } from 'react';
import { api, fetchMe } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Avatar, Icon, PAGE_SIZE, Pagination, fmtNum } from '../common';
import { filterUsers } from './filter';
import { useAccountModal } from './AccountModalProvider';
import { AccountTabSkeleton } from './AccountTabSkeleton';
import { RoleBadge, AccountError } from './tabUi';
import { fmtAdded } from './utils';
import { relTime, sectionLabel, emailKey, WINDOW_COVERAGE_PHRASE } from './activityFormat';
import type { RoleFilter, User } from './types';
import type { ActivityOverviewRow, ActivityWindow } from '@bbbk/schema';

// Account access — the customer-facing twin of the Dogs & Owners list, on the
// house pattern: a stat strip, the shared search + role-filter toolbar (lifted to
// AccountPage), the shared table primitive, client-side pagination. Edit/add open
// the ONE global edit-user modal (AccountModalProvider) — the same modal reached
// by clicking a user's avatar anywhere — so there's a single launch point.
// The API gates every call to admins and guards self-removal/self-demotion; this
// view surfaces that and covers all states (loading / empty / populated / error).

interface AccountViewProps {
  // The shared time window for the usage columns (owned by AccountPage).
  window: ActivityWindow;
  // The shared toolbar's search + role filter, owned by AccountPage so all three
  // tabs read the same values from one bar.
  q: string;
  filter: RoleFilter;
  // The allowlist, fetched once by AccountPage and shared with the other tabs.
  // null while loading; an edit revalidates it via the mutation bus + the modal
  // host's reload, so this table refreshes without its own write path.
  users: User[] | null;
  usersError: boolean;
}

export function AccountView({ window, q, filter, users, usersError }: AccountViewProps) {
  const [myEmail, setMyEmail] = useState('');
  // The one global edit-user modal — opened here by a row click, and by a user's
  // avatar anywhere else. Always present inside the admin shell.
  const modal = useAccountModal();
  const openEdit = (email: string) => modal?.openUserEdit(email);

  // Usage metrics for the columns — the app's SWR engine, keyed by window (same
  // pattern as the heatmap + the user list). Keyed-by-emailKey map so the citext
  // actor_email joins the allowlist email (case-insensitive).
  const overview = useResource(`activity:overview:${window}`, () =>
    api<ActivityOverviewRow[]>(`/activity/overview?window=${window}`).then((r) => r.data),
  );
  const metrics = useMemo<Record<string, ActivityOverviewRow>>(
    () => Object.fromEntries((overview.state.data ?? []).map((m) => [emailKey(m.email), m])),
    [overview.state.data],
  );

  // Paging (the search + role filter are the shared toolbar's, passed in).
  const [page, setPage] = useState(1);

  // The signed-in email — to badge "You" and block removing your own row. Goes
  // through the client's fetchMe (the /me body is un-enveloped), so no call site
  // hand-rolls a fetch.
  useEffect(() => {
    fetchMe().then((m) => m?.email && setMyEmail(emailKey(m.email)));
  }, []);

  // A new search or filter starts at page 1 (AC3).
  useEffect(() => setPage(1), [q, filter]);


  const all = users ?? [];
  const filtered = useMemo(() => filterUsers(all, q, filter), [all, q, filter]);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="account-list">
      {/* The chart cards and the shared search/role toolbar are page chrome
          (AccountPage). This List body just consumes (q, filter); the page's
          .account-view owns the outer padding so all elements align. */}
      {usersError && <AccountError message="Couldn't load account access. Try again." />}

      {users === null ? (
        <AccountTabSkeleton label="Loading account access…" />
      ) : all.length === 0 ? (
        <div className="account-empty">
          <span className="account-empty-mark">
            <Icon name="user" size={22} />
          </span>
          <div className="account-empty-title">No one can sign in yet</div>
          <div className="account-empty-sub">Use “Add user” above to grant the first access.</div>
        </div>
      ) : (
        <>
          <div className="bookings-table account-table" role="table">
            <div className="bt-row head" role="row">
              <div role="columnheader">Person</div>
              <div role="columnheader">Role</div>
              <div role="columnheader">Last seen</div>
              <div role="columnheader">Logins</div>
              <div role="columnheader">Last section</div>
              <div role="columnheader">Created by</div>
              <div role="columnheader">Added</div>
              <div role="columnheader" aria-label="Actions" />
            </div>
            {pageRows.map((u) => {
              const isMe = emailKey(u.email) === myEmail;
              const m = metrics[emailKey(u.email)];
              const seen = m?.lastSeen ?? null;
              return (
                <div
                  key={u.email}
                  className="bt-row clickable"
                  role="row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openEdit(u.email)}
                  title="Edit this person"
                >
                  <div className="account-person" role="cell">
                    <Avatar name={u.email} id={u.email} size={30} />
                    <span className="account-email">{u.email}</span>
                    {isMe && <span className="account-you">You</span>}
                  </div>
                  <div role="cell">
                    <RoleBadge role={u.role} />
                  </div>
                  <div className={'account-seen' + (seen ? '' : ' muted')} role="cell">
                    {relTime(seen)}
                  </div>
                  <div className={'account-logins mono' + (m?.logins ? '' : ' muted')} role="cell">
                    {fmtNum(m?.logins ?? 0)}
                  </div>
                  <div className={'account-section' + (m?.lastSection ? '' : ' muted')} role="cell">
                    {sectionLabel(m?.lastSection ?? null)}
                  </div>
                  <div className="account-by" role="cell">
                    {u.addedBy ?? 'Seeded'}
                  </div>
                  <div className="account-added" role="cell">
                    {fmtAdded(u.addedAt)}
                  </div>
                  <div className="account-cell-action" role="cell">
                    <button
                      className="account-remove"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        openEdit(u.email);
                      }}
                      disabled={isMe}
                      aria-label={isMe ? 'You can’t remove your own access' : `Remove ${u.email}`}
                      title={isMe ? 'You can’t remove your own access' : 'Remove access'}
                    >
                      <Icon name="trash" size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="empty-state pad-30">
                No matches.
              </div>
            )}
          </div>

          <Pagination page={page} total={filtered.length} onPage={setPage} />

          {/* Last seen / Logins / Last section all come from the SAME windowed
              /activity/overview the chart cards read, so the footnote names that
              window here too — otherwise a bounded login count reads as a
              lifetime one, exactly as the unlabelled cards did. */}
          <p className="account-roles-hint">
            <strong>Last seen</strong>, <strong>Logins</strong> and <strong>Last section</strong> cover{' '}
            {WINDOW_COVERAGE_PHRASE[window]}. <strong>Admin</strong> manages who can sign in.{' '}
            <strong>Owner</strong> and <strong>Helper</strong> are staff with full day-to-day access.
          </p>
        </>
      )}
    </div>
  );
}
